import { useState, useCallback } from 'react';

/**
 * Custom hook for managing ConfirmDialog state
 * Issue #1395 - Replace native confirm() dialogs with accessible modals
 *
 * Usage:
 * ```tsx
 * const { confirmDialog, showConfirm, closeDialog, ConfirmDialogComponent } = useConfirmDialog();
 *
 * // Show a confirmation dialog
 * const handleDelete = () => {
 *   showConfirm({
 *     title: 'Delete Item',
 *     message: 'Are you sure?',
 *     onConfirm: async () => {
 *       await api.deleteItem(id);
 *       toast.success('Deleted');
 *     },
 *     variant: 'danger'
 *   });
 * };
 *
 * // Render the dialog component
 * return (
 *   <div>
 *     <button onClick={handleDelete}>Delete</button>
 *     <ConfirmDialogComponent />
 *   </div>
 * );
 * ```
 */

export interface ConfirmDialogState {
  isOpen: boolean;
  title: string;
  message: string;
  confirmText?: string;
  cancelText?: string;
  variant?: 'danger' | 'primary';
  onConfirm: () => void | Promise<void>;
  isProcessing: boolean;
}

export interface ShowConfirmOptions {
  title: string;
  message: string;
  confirmText?: string;
  cancelText?: string;
  variant?: 'danger' | 'primary';
  onConfirm: () => void | Promise<void>;
}

const initialState: ConfirmDialogState = {
  isOpen: false,
  title: '',
  message: '',
  confirmText: 'Confirm',
  cancelText: 'Cancel',
  variant: 'danger',
  onConfirm: () => {},
  isProcessing: false
};

export const useConfirmDialog = () => {
  const [confirmDialog, setConfirmDialog] = useState<ConfirmDialogState>(initialState);

  /**
   * Show a confirmation dialog
   * @param options Dialog configuration options
   */
  const showConfirm = useCallback((options: ShowConfirmOptions) => {
    setConfirmDialog({
      isOpen: true,
      title: options.title,
      message: options.message,
      confirmText: options.confirmText || 'Confirm',
      cancelText: options.cancelText || 'Cancel',
      variant: options.variant || 'danger',
      onConfirm: async () => {
        // Set processing state
        setConfirmDialog(prev => ({ ...prev, isProcessing: true }));

        try {
          // Execute the confirm action
          await options.onConfirm();

          // Close dialog on success
          setConfirmDialog(prev => ({ ...prev, isOpen: false, isProcessing: false }));
        } catch (error) {
          // Reset processing state on error (keep dialog open)
          setConfirmDialog(prev => ({ ...prev, isProcessing: false }));
          // Re-throw error so caller can handle it
          throw error;
        }
      },
      isProcessing: false
    });
  }, []);

  /**
   * Close the confirmation dialog
   */
  const closeDialog = useCallback(() => {
    setConfirmDialog(prev => ({ ...prev, isOpen: false }));
  }, []);

  /**
   * Reset dialog to initial state
   */
  const resetDialog = useCallback(() => {
    setConfirmDialog(initialState);
  }, []);

  return {
    confirmDialog,
    showConfirm,
    closeDialog,
    resetDialog
  };
};

export default useConfirmDialog;
